import { cardTypes } from './card_types'
import { energyTypes } from './energy_types'
import { games } from './games'
import { rarities } from './rarities'
import { stocks } from './stocks'
import { views } from './views'
import { queryStringToNumberArray } from '../helpers/query_string_to_number_array'
import { queryStringToNumberArrayFromSelection } from '../helpers/query_string_to_number_array_from_selection'

const categorySorts = [
  'updated_at_desc',
  'release_date_desc',
  'release_date_asc',
  'price_desc',
  'price_asc',
  'name_ja_asc',
  'name_ja_desc',
] as const

const profileSorts = [
  'ordinality_asc',
  'created_desc',
  'price_desc',
  'price_asc',
  'release_date_desc',
  'release_date_asc',
  'name_ja_asc',
  'name_ja_desc',
] as const

const graderCompanies = ['PSA', 'BGS', 'CGC', 'ARS'] as const

type BrowseFilters = Readonly<{
  query: string
  sort: string
  stocks: string[]
  view: string
  game?: number
  cardTypes: number[]
  energyTypes: number[]
  rarities: number[]
  conditions: number[]
  minPrice?: number
  maxPrice?: number
  userId?: number
  grader?: string
}>

const isCategoryBrowse = (pathname: string) => pathname === '/' || pathname.startsWith('/categories')

const isProfileBrowse = (pathname: string) => pathname.startsWith('/u/') || pathname.startsWith('/collections/')

const isBrowseSearchPath = (pathname: string) => {
  const path = pathname.replace(/\/+$/, '') || '/'
  return path === '/' || path === '/categories' || /^\/(?:categories\/\d+|(?:u|collections)\/(?:\d+|me))$/.test(path)
}

const sortsForPath = (pathname: string): readonly string[] => isProfileBrowse(pathname) ? profileSorts : categorySorts

const defaultSortForPath = (pathname: string) => sortsForPath(pathname)[0]

const searchParamsFrom = (search: string) => new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

const optionalNumber = (raw: string | null): number | undefined => {
  if (!raw || Number.isNaN(Number(raw))) return undefined
  return Number(raw)
}

const readBrowseFilters = (search: string, pathname: string): BrowseFilters => {
  const params = searchParamsFrom(search)
  const viewRaw = params.get('view') ?? ''
  const gameRaw = optionalNumber(params.get('game'))
  const game = gameRaw !== undefined && games.some(g => g.id === gameRaw) ? gameRaw : undefined
  const graderRaw = (params.get('grader') ?? '').toUpperCase()
  const grader = (graderCompanies as readonly string[]).includes(graderRaw) ? graderRaw : undefined

  return {
    query: params.get('query') ?? (isBrowseSearchPath(pathname) ? (params.get('q') ?? '') : ''),
    sort: sortsForPath(pathname).includes(params.get('sort') ?? '') ? (params.get('sort') as string) : defaultSortForPath(pathname),
    stocks: params.has('stocks')
      ? (params.get('stocks') ?? '').split(',').map(stock => stock.trim()).filter(stock => stocks.includes(stock))
      : (isCategoryBrowse(pathname) ? ['for_sale'] : []),
    view: viewRaw === 'items' || viewRaw === 'listings' ? viewRaw : views[0],
    game,
    cardTypes: params.get('card_types') ? queryStringToNumberArrayFromSelection(params.get('card_types') as string, cardTypes) : [],
    energyTypes: params.get('energy_types') ? queryStringToNumberArrayFromSelection(params.get('energy_types') as string, energyTypes) : [],
    rarities: params.get('rarities') ? queryStringToNumberArrayFromSelection(params.get('rarities') as string, rarities) : [],
    conditions: params.get('conditions') ? queryStringToNumberArray(params.get('conditions') as string, 100) : [],
    minPrice: optionalNumber(params.get('min_price')),
    maxPrice: optionalNumber(params.get('max_price')),
    userId: optionalNumber(params.get('user_id')),
    grader,
  }
}

const isAvailableOnly = (selected: string[]) => selected.length === 1 && selected[0] === 'for_sale'

const stocksForAvailableOnly = (on: boolean): string[] => on ? ['for_sale'] : ['for_sale', 'sold']

const stocksSearchValue = (selected: string[], pathname: string): string | undefined => {
  if (selected.length === 0) return undefined
  if (isCategoryBrowse(pathname) && isAvailableOnly(selected)) return undefined
  return selected.join(',')
}

const soldRequestParam = (selected: string[]): string | undefined => selected.length === 1 ? selected[0] : undefined

const withSearchParam = (search: string, key: string, value?: string) => {
  const params = searchParamsFrom(search)
  if (!value) params.delete(key)
  else params.set(key, value)
  return params.toString()
}

const searchForAvailableOnly = (search: string, pathname: string, on: boolean) => withSearchParam(search, 'stocks', stocksSearchValue(stocksForAvailableOnly(on), pathname))

const searchForSort = (search: string, pathname: string, sort: string) => withSearchParam(search, 'sort', sort === defaultSortForPath(pathname) ? undefined : sort)

const textMatchesQuery = (values: Array<string | undefined>, query: string) => {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return values.some(value => (value ?? '').toLowerCase().includes(needle))
}

const canonicalBrowseSearch = (search: string, pathname: string): string | undefined => {
  if (!isBrowseSearchPath(pathname)) return undefined
  const params = searchParamsFrom(search)
  if (!params.has('q')) return undefined
  if (!params.has('query')) {
    const alias = params.get('q')
    if (alias) params.set('query', alias)
  }
  params.delete('q')
  return params.toString()
}

const hrefWithSearch = (pathname: string, search: string) => search ? `${pathname}?${search}` : pathname

const withSortFrom = (nextSearch: string, currentSearch: string) => {
  const params = searchParamsFrom(nextSearch)
  const sort = searchParamsFrom(currentSearch).get('sort')
  if (sort) params.set('sort', sort)
  else params.delete('sort')
  return params.toString()
}

const categoryBrowsePath = (tagId?: number, leaveCategory = false) => !leaveCategory && tagId ? `/categories/${tagId}` : '/categories'

const categoryPath = (tid: string | undefined, filterSearch: string, currentSearch: string, page?: number) => {
  const search = withSortFrom(filterSearch, currentSearch)
  let path = tid ? `/categories/${tid}` : '/categories'
  if (search) path += `?${search}`
  if (page && page > 0) path += `${search ? '&' : '?'}page=${String(page)}`
  return path
}

const choiceChipCount = (selected: number, total: number) => {
  if (selected <= 0) return 0
  return selected === total ? 1 : selected
}

export {
  canonicalBrowseSearch,
  categoryBrowsePath,
  categoryPath,
  categorySorts,
  choiceChipCount,
  defaultSortForPath,
  graderCompanies,
  hrefWithSearch,
  isAvailableOnly,
  isCategoryBrowse,
  readBrowseFilters,
  searchForAvailableOnly,
  searchForSort,
  soldRequestParam,
  sortsForPath,
  stocksForAvailableOnly,
  textMatchesQuery,
  withSearchParam,
  withSortFrom,
}
export type { BrowseFilters }
